import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BreadcrumbComponent,
  SearchInputComponent,
  AlertComponent,
} from "../../common/components";
import { routes } from "../../constants/routes";
import { Box, Tab, Tabs, Button, Menu, MenuItem } from "@mui/material";
import "./guides.scss";
import CustomTabPanel from "../surveys/email-templates/customized-template-tab-panel";
import GuidesList from "./guides-list";
import AddGuidePopup from "./add-guide";
import { useLogin } from "../../common/hooks/useUser";
import { useSetLoading } from "../../common/hooks/appearance";
import { getGuideList } from "../../api/guides";
import { convertUTCToLocal } from "../../utils/utils";

const Guides = () => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchor);
  const [indexValue, setIndexValue] = useState(0);
  const [searchText, setSearchText] = useState<string>("");
  const [isResetSearch, setIsResetSearch] = useState<boolean>(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] =
    useState<string>("All Text Guides");
  const [showAddGuidePopup, setShowAddGuidePopup] = useState<boolean>(false);
  const [selectedGuideType, setSelectedGuideType] = useState<"Video" | "Text">(
    "Video"
  );
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [data, setData] = React.useState<any[]>([]);
  const [pageSize,setPageSize] = React.useState<number>(selectedGuideType === "Video" ? 15 : 10);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const tenantDetails = useLogin();
  const isSuperTenant = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const getCategories = () => {
    const result: any[] = [];
    const permissisonList =
      tenantDetails?.userDetails?.roles?.rolePermissions || [];
    const collectSubResources = (permission: any) => {
      if (permission?.scopes?.read === true) {
        result.push(permission.resource);
      }
      if (Array.isArray(permission?.subresources)) {
        permission?.subresources?.forEach((subperm: any) => {
          collectSubResources(subperm);
        });
      }
    };
    permissisonList.forEach((permission: any) => {
      if (permission?.scopes?.read === true) {
        result.push(permission.resource);
      }
      if (Array.isArray(permission?.subresources)) {
        permission?.subresources?.forEach((subperm: any) => {
          collectSubResources(subperm);
        });
      }
    });

    return result;
  };

  useEffect(() => {
    const fetchedCategories = getCategories();
    const currentGuideType = indexValue === 0 ? "Text" : "Video";
    const allCategoryName = `All ${currentGuideType} Guides`;
    const extraCategories = ["Miscellaneous", "Support"];

    const updatedCategories = [...fetchedCategories];
    const leadReportsIndex = updatedCategories.indexOf("Lead Reports");

    // If "Lead Reports" exists, insert "Lead Logs" after it
    if (leadReportsIndex !== -1) {
      updatedCategories.splice(leadReportsIndex + 1, 0, "Lead Logs");
    } else {
      // Otherwise, push "Lead Logs" at the end
      updatedCategories.push("Lead Logs");
    }

    const filteredCategories = [
      allCategoryName,
      "Dashboard",
      ...updatedCategories,
      ...extraCategories,
    ];
    const uniqueCategories = Array.from(new Set(filteredCategories));
    setCategories(uniqueCategories);

    if (selectedCategory === "All" || selectedCategory.startsWith("All ")) {
      setSelectedCategory(allCategoryName);
    }
  }, [indexValue]);

  const crumbMapping = [
    {
      label: "Guides",
      path: routes.GUIDES,
      handleClick: () => {
        setIndexValue(0);
      },
    },
  ];

  const addGuidesButtonOptions = () => {
    return [
      // {
      //   value: "Add Text Guide",
      //   id: 1,
      // },
      {
        value: "Add Video Guide",
        id: 2,
      },
    ];
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const handleAddGuides = (selectedOption: { value: string; id: number }) => {
    const guideType = selectedOption.id === 1 ? "Text" : "Video";
    setSelectedGuideType(guideType);
    setShowAddGuidePopup(true);
    handleClose();
  };

  const handleCloseAddGuidePopup = () => {
    setShowAddGuidePopup(false);
  };
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentPage(1);
    setIndexValue(newValue);
    if (selectedCategory.startsWith("All ")) {
      const currentGuideType = newValue === 0 ? "Text" : "Video";
      setSelectedCategory(`All ${currentGuideType} Guides`);
    }
    setPageSize(newValue === 0 ? 15 : 10)
  };

  const onSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    setIsResetSearch(false);
  };

  const fetchGuidesData = async (
    page = 1,
    search: string = "",
    mediaType: "Video" | "Text" = "Video"
  ) => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params = {
        limit: pageSize,
        offset: offset,
        // category: selectedCategory.startsWith("All ")
        //   ? "All"
        //   : selectedCategory,
        mediaType: mediaType,
        ...(search && { searchText: search }),
      };
      const response = await getGuideList(params);
      const formatData = response?.data?.data?.guides?.map(
        (data: any, index: number) => {
          const mediaKey = data?.media_key;
          const fileName = mediaKey ? mediaKey.split("/").pop() : "";
          const displayName =
            fileName?.split("_")?.slice(1).join("_") || fileName;
          const fileExtension = fileName
            ? fileName.split(".").pop()?.toUpperCase()
            : "";
          return {
            id: data?.id,
            category: data?.category,
            title: data?.title,
            url: `${process.env.REACT_APP_S3_BULK_UPLOAD_BUCKET_DOMAIN}/${data?.media_key}`,
            previewLink: data?.preview_key
              ? `${process.env.REACT_APP_S3_BULK_UPLOAD_BUCKET_DOMAIN}/${data?.preview_key}`
              : "",
            thumbnail: data?.thumbnail_key
              ? `${process.env.REACT_APP_S3_BULK_UPLOAD_BUCKET_DOMAIN}/${data?.thumbnail_key}`
              : null,
            fileName: displayName,
            mediaType: fileExtension || "UNKNOWN",
            uploadedBy: [
              data?.createdBy?.first_name,
              data?.createdBy?.last_name,
            ]
              .filter(Boolean)
              .join(" "),
            uploadedTime: convertUTCToLocal(data?.created_at),
          };
        }
      );
      setData(formatData || []);
      setTotalRecords(response?.data?.data?.totalCount || 0);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching guides data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGuidesData(
      currentPage,
      searchText,
      indexValue === 0 ? "Video" : "Video"
    );
  }, [searchText, currentPage, selectedCategory, indexValue]);

  const fetchGuides = () => {
    if (currentPage === 1) {
      fetchGuidesData(
        currentPage,
        searchText,
        indexValue === 0 ? "Video" : "Video"
      );
    } else {
      //updated record comes on first page after creation
      //setting current page to 1 will trigger useEffect to fetch data
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    setPageSize(selectedGuideType === "Video" ? 15 : 10);
  }, [selectedGuideType]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div className="guides-page">
      <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      <div className="guides-wrapper">
        <div className="guides-header">
          <div className="guides-search">
            <SearchInputComponent
              placeholder="Search by Title, Category..."
              onSearch={onSearch}
              isReset={isResetSearch}
              ref={searchRef}
            />
          </div>
          {isSuperTenant && (
            <div>
              <Button
                variant="contained"
                onClick={handleClick}
                className="custom-button"
              >
                Add New Guide
              </Button>
              <Menu anchorEl={menuAnchor} open={open} onClose={handleClose}>
                {addGuidesButtonOptions().map((option) => (
                  <MenuItem
                    key={option.id}
                    onClick={() => handleAddGuides(option)}
                  >
                    {option.value}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          )}
        </div>
        <div className="guides-content">
          {/* <div className="categories-section">
            <h4>Categories</h4>
            <ul className="categories-list">
              {categories.map((category, index) => (
                <li
                  key={index}
                  className={selectedCategory === category ? "active" : ""}
                  onClick={() => {
                    if (selectedCategory !== category) {
                      setSelectedCategory(category);
                      setCurrentPage(1);
                      setTotalRecords(0);
                    }
                  }}
                >
                  {category}
                  {selectedCategory === category &&
                    totalRecords > 0 &&
                    !isLoading &&
                    ` (${totalRecords})`}
                </li>
              ))}
            </ul>
          </div> */}
          <div className="guide-tabs-section">
            <Box className="guide-tabs-wrapper">
              <Tabs
                value={indexValue}
                allowScrollButtonsMobile
                variant="scrollable"
                onChange={handleTabChange}
                className="guide-tabs"
              >
                {/* <Tab label="Text Guides" /> */}
                <Tab label="Tutorial Videos" />
              </Tabs>
            </Box>
            <>
              {/* <CustomTabPanel value={indexValue} index={0}>
                <GuidesList
                  mediaType="Text"
                  searchText={searchText}
                  selectedCategory={selectedCategory}
                  setIsLoading={setIsLoading}
                  totalRecords={totalRecords}
                  setTotalRecords={setTotalRecords}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  data={data}
                  pageSize={pageSize}
                  fetchGuides={fetchGuides}
                  isSuperTenant={isSuperTenant}
                  setAlertMessage={setAlertMessage}
                  setAlertOpen={setAlertOpen}
                  setAlertType={setAlertType}
                />
              </CustomTabPanel> */}
              <CustomTabPanel value={indexValue} index={0}>
                <GuidesList
                  mediaType="Video"
                  searchText={searchText}
                  selectedCategory={selectedCategory}
                  setIsLoading={setIsLoading}
                  totalRecords={totalRecords}
                  setTotalRecords={setTotalRecords}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  data={data}
                  pageSize={pageSize}
                  fetchGuides={fetchGuides}
                  setAlertMessage={setAlertMessage}
                  setAlertOpen={setAlertOpen}
                  setAlertType={setAlertType}
                />
              </CustomTabPanel>
            </>
          </div>
        </div>
      </div>

      <AddGuidePopup
        open={showAddGuidePopup}
        mediaType={selectedGuideType}
        onClose={handleCloseAddGuidePopup}
        setIsLoading={setIsLoading}
        categories={categories.filter((cat) => !cat.startsWith("All "))}
        setAlertMessage={setAlertMessage}
        setAlertOpen={setAlertOpen}
        setAlertType={setAlertType}
        setSelectedCategory={setSelectedCategory}
        setIndexValue={setIndexValue}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        selectedCategory={selectedCategory}
        tabIndexValue={indexValue}
        fetchGuides={fetchGuides}
      />
      <AlertComponent
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        type={alertType}
        message={alertMessage}
      />
    </div>
  );
};

export default Guides;
